import React, { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import api from "../hooks/api";
import { Button } from "react-bulma-components";
import { File, UploadCloud } from "lucide-react";
import { API_URLS } from "../App";
import ListItems from "../components/CompoundManageListPage/ListItems";
import { useLayoutContext } from "../context/LayoutContext";
import { ManagementCompound } from "../model/ManagementCompound";

interface FilterParams {
  search: string;
  page: number;
  per_page: number;
  sort_by: string;
  sort_direction: "asc" | "desc";
  stock_status: string;
}

/**
 * Componente para realizar o upload de arquivos Excel para o backend.
 */
const ImportExcelPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const {
    userSettings,
    setUserSettings,
    systemError,
    setSystemError,
    loading,
    setLoading,
    error,
    setError,
    success,
    setSuccess,
    modalScreen,
    setModalScreen,
    page,
    setPage,
  } = useLayoutContext();
  const [file, setFile] = useState<File | null>(null);
  const [uploading, setUploading] = useState(false);
  const [importedData, setImportedData] = useState<ManagementCompound[]>([]);
  const [filters, setFilters] = React.useState<FilterParams>({
    search: "",
    page: 1,
    per_page: 100,
    sort_by: "name",
    sort_direction: "asc",
    stock_status: "",
  });

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setError(null); // Limpa qualquer erro anterior
    setFile(null);
    const selectedFile = event.target.files?.[0];
    if (selectedFile) {
      if (
        selectedFile.type ===
          "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" || // .xlsx
        selectedFile.type === "application/vnd.ms-excel"
      ) {
        setFile(selectedFile);
      } else {
        setFile(null);
        setError(
          "Tipo de arquivo inválido. Por favor, selecione um arquivo .xlsx ou .xls.",
        );
      }
    }
  };

  const handleUpload = async () => {
    if (!file) {
      setError("Por favor, selecione um arquivo para importar.");
      return;
    }

    setUploading(true);
    setError(null);

    const formData = new FormData();
    formData.append("file", file);

    try {
      const response = await api.postForm(API_URLS.IMPORT, formData);
      if (response.status === 201 || response.status === 200) {
        setSuccess("Arquivo importado com sucesso!");
        setFile(null);
        setImportedData(response.data);
      } else {
        const errorMessage = await response.data;
        throw new Error(
          `Erro ao importar arquivo: ${response.status} - ${errorMessage}`,
        );
      }
    } catch (err: any) {
      setError(err.message || "Ocorreu um erro ao enviar o arquivo.");
    } finally {
      setUploading(false);
      setFile(null);
    }
  };

  const handleViewItem = (
    id: string,
    e?: React.MouseEvent<HTMLAnchorElement>,
  ) => {
    history.replaceState(
      { fromImport: true, data: importedData },
      "Import Excel Page",
    );
  };

  const handleEditItem = (
    id: string,
    e?: React.MouseEvent<HTMLAnchorElement>,
  ) => {
    history.replaceState(
      { fromImport: true, data: importedData },
      "Import Excel Page",
    );
  };

  const handleDelete = async (
    id: string,
    e?: React.MouseEvent<HTMLAnchorElement>,
  ) => {
    e?.preventDefault();
    if (window.confirm("Você tem certeza que deseja excluir este item?")) {
      try {
        await api.delete(`${API_URLS.COMPOUNDS}/${encodeURIComponent(id)}`);
        setImportedData((prevData) =>
          prevData.filter((dataItem) => dataItem.id !== id),
        );
        setSuccess("Item excluído com sucesso.");
      } catch (err: any) {
        setError(err.message || "Erro ao excluir o item.");
      }
    }
  };

  useEffect(() => {
    if (history.state?.fromImport) {
      setImportedData(history.state?.data);
      history.replaceState({}, "Import Excel Page");
    }
  });

  return (
    <>
      {importedData.length == 0 ? (
        <div className="min-h-screen p-4 flex items-center justify-center">
          <div className="bg-gray-800 rounded-xl shadow-2xl p-8 w-full max-w-md space-y-6">
            <span className="text-2xl font-bold text-white flex items-center gap-3">
              <UploadCloud className="w-6 h-6 text-blue-400" />
              Importar PCs do Excel/CSV
            </span>

            <div className="space-y-4">
              <div className="file has-name">
                <label className="file-label">
                  <input
                    className="file-input"
                    type="file"
                    name="file"
                    onChange={handleFileChange}
                    accept=".xlsx,.xls"
                    disabled={uploading}
                  />
                  <span className="file-cta">
                    <span className="file-icon">
                      <File className="w-4 h-4" />
                    </span>
                    <span className="file-label">Escolher Arquivo…</span>
                  </span>
                  <span className="file-name">
                    {file ? file.name : "Nenhum arquivo selecionado"}
                  </span>
                </label>
              </div>
            </div>

            <Button
              onClick={handleUpload}
              disabled={uploading}
              className={`w-full ${uploading ? "is-loading" : "bg-blue-600 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded-md transition-colors duration-200"}`}
            >
              {uploading ? "Enviando..." : "Importar"}
            </Button>
          </div>
        </div>
      ) : (
        <div className="container mx-auto p-4">
          <div className="p-4 overflow-auto">
            <p className="text-2xl font-bold mb-4">Itens Importados</p>
            <ListItems
              data={importedData}
              onDelete={handleDelete}
              onView={handleViewItem}
              onEdit={handleEditItem}
              setModalScreen={setModalScreen}
              totalResults={importedData.length}
              totalPages={1}
              loading={false}
              filters={filters}
              setFilters={setFilters}
            />
          </div>
        </div>
      )}
    </>
  );
};

export default ImportExcelPage;
